import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';
import { FilterCategories } from '../../interfaces/routering/filter-categories';
import { HeadCategory } from '../../interfaces/routering/head-category';
import { SubCategory } from '../../interfaces/routering/sub-category';
import { ReportService } from '../../services/reports/report.service';
import { LatLngLiteral } from 'leaflet';

@Injectable()
export class SidenavService implements OnDestroy {

  mobileBreakpoint: string = 'lt-md';

  // mat-sidenav open status
  private _openSubject = new BehaviorSubject<boolean>(this.mediaObserver.isActive(this.mobileBreakpoint));
  open$ = this._openSubject.asObservable();

  // mat-sidenav mode status
  private _modeSubject = new BehaviorSubject<'side' | 'over'>(this.mediaObserver.isActive(this.mobileBreakpoint) ? 'over' : 'side');
  mode$ = this._modeSubject.asObservable();

  // Head & Sub Categories for mat-sidenav
  public menuItems: HeadCategory[] = [];
  private _selected$ = new BehaviorSubject<FilterCategories[]>([]);
  readonly selected$ = this._selected$.asObservable();

  get selected(): FilterCategories[] {
    return this._selected$.value;
  }

  // did user search for an address to zoom into?
  public updateMap$: Subject<LatLngLiteral> = new Subject<LatLngLiteral>();

  private destroy$: Subject<any> = new Subject<any>();

  constructor(private mediaObserver: MediaObserver,
              private reportService: ReportService,
  ) {
    this.mediaObserver.asObservable().pipe(
      map(() => this.mediaObserver.isActive(this.mobileBreakpoint)),
      takeUntil(this.destroy$)
    ).subscribe((isMobile: boolean): void => {
      if (isMobile) {
        this._openSubject.next(false);
        this._modeSubject.next('over');
      } else {
        this._openSubject.next(true);
        this._modeSubject.next('side');
      }
    });

    this.reportService.categories().subscribe((categories: HeadCategory[]): void => {
      if (categories) {
        if (categories.length) {
          this.menuItems = categories.map((item: HeadCategory): HeadCategory => ({
            ...item,
            subIds: item.sub_categories.map((s: SubCategory): number => s.id)
          }));

          const ids: FilterCategories[] = [];
          categories.forEach((category: HeadCategory): void => {
            const sub: number[] = [];
            if (category.sub_categories.length) {
              category.sub_categories.forEach((subId: SubCategory): void => {
                sub.push(subId.id);
              });
            }

            ids.push({
              id: category.id,
              subIds: sub,
            });
          });

          this._selected$.next(ids);
        }
      }
    });
  }

  open(): void {
    this._openSubject.next(true);
  }

  close(): void {
    this._openSubject.next(false);
  }

  toggle(parentId: number, subId?: number, allSubIds?: number[]): void {
    const current: FilterCategories[] = [...this.selected];
    const headIndex: number = current.findIndex((c: FilterCategories): boolean => c.id === parentId);

    // toggle head category
    if (!subId) {
      if (headIndex === -1) {
        current.push({id: parentId, subIds: allSubIds ? [...allSubIds] : []});
      } else {
        current.splice(headIndex, 1);
      }
      this._selected$.next(current);

      return;
    }

    // toggle sub category
    if (headIndex === -1) {
      current.push({id: parentId, subIds: [subId]});
    } else {
      const head: FilterCategories = {...current[headIndex], subIds: [...current[headIndex].subIds]};
      const subIndex: number = head.subIds.indexOf(subId);

      if (subIndex === -1) {
        head.subIds.push(subId);
      } else {
        head.subIds.splice(subIndex, 1);
      }

      if (head.subIds.length === 0) {
        current.splice(headIndex, 1);
      } else {
        current[headIndex] = head;
      }
    }

    this._selected$.next(current);
  }

  isChecked(parentId: number, subId?: number, allSubIds?: number[]): boolean {
    const head: FilterCategories = this.selected.find((c: FilterCategories): boolean => c.id === parentId);
    if (!head) {
      return false;
    }

    if (!subId && allSubIds) {
      return head.subIds.length === allSubIds.length;
    }

    return subId ? head.subIds.includes(subId) : false;
  }

  isIndeterminate(parentId: number, allSubIds?: number[]): boolean {
    const head: FilterCategories = this.selected.find((c: FilterCategories): boolean => c.id === parentId);
    if (!head || !allSubIds?.length) {
      return false;
    }

    return head.subIds.length > 0 && head.subIds.length < allSubIds.length;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }
}
